import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';
import Dashboard from './components/dashboards/Dashboard';
import Login from './components/admin/Login';
import CollectionDetails from './components/shopify/CollectionDetails';
import Catalogue from './components/shopify/Catalogue';
import ProductDetails from './components/shopify/ProductDetails';
import ImportScreen from './components/admin/ImportScreen';
import Fulfillment from './components/warehouses/Fulfillment';
import Returns from './components/returns/Returns';
import Tickets from './components/support/Tickets';
import Warehouses from './components/warehouses/Warehouses';
import AddWarehouse from './components/warehouses/AddWarehouse';
import EditWarehouse from './components/warehouses/EditWarehouse';
import Integrations from './components/integrations/Integrations';
import Trends from './components/trends/Trends';
import GoogleSearchTrends from './components/trends/GoogleSearchTrends';
import { GoogleMapsProvider } from './contexts/GoogleMapsContext';
import ReturnDetails from './components/returns/ReturnDetails';
import Admin from './components/admin/Admin';
import AdminRoute from './services/AdminRoute';
import { authService } from './services/auth';
import Onboarding from './components/onboarding/Onboarding';
import Ledger from './components/ledger/Ledger';
import Payouts from './components/admin/Payouts';
import CelebritiesManager from './components/admin/CelebritiesManager';
import BrandsOnboarding from './components/admin/BrandsOnboarding';
import BrandGallery from './components/admin/BrandGallery';
import OrderItemDetails from './components/orders/OrderItemDetails';
import KeywordsBlacklist from './components/admin/outfits/KeywordsBlacklist';
import CCIBlacklist from './components/admin/outfits/CCIBlacklist';
import TrendBuilder from './components/admin/outfits/TrendBuilder';
import OutfitAdminGetStarted from './components/admin/outfits/OutfitAdminGetStarted';
import ProductsManager from './components/admin/ProductsManager';

// Get Google Maps API key from environment variables
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyBaXXAr9c51VmI88ysKtmioXkPIhG05GXU";

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = localStorage.getItem('isLoggedIn') === 'true';

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Logout Route component
const LogoutRoute = () => {
  authService.logout();
  return null;
};

// AppRoutes component to handle all routes and navigation
function AppRoutes() {
  const [email, setEmail] = useState(() => localStorage.getItem('userEmail') || '');

  const handleLogout = () => {
    setEmail('');
    authService.logout();
  };

  return (
    <GoogleMapsProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/logout" element={<LogoutRoute />} />

        {/* Protected Routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard email={email} onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/ledger" element={
          <ProtectedRoute>
            <Ledger email={email} onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/trends" element={
          <ProtectedRoute>
            <Trends email={email} onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/trends/google-search" element={
          <ProtectedRoute>
            <GoogleSearchTrends email={email} onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/c/:handle/products" element={
          <ProtectedRoute>
            <Catalogue email={email} onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/c/:handle" element={
          <ProtectedRoute>
            <CollectionDetails email={email} onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/product/:handle" element={
          <ProtectedRoute>
            <ProductDetails email={email} onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/product/import/:handle" element={
          <ProtectedRoute>
            <ImportScreen email={email} onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/orders" element={
          <ProtectedRoute>
            <Fulfillment email={email} onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/returns" element={
          <ProtectedRoute>
            <Returns email={email} onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/returns/:orderId" element={
          <ProtectedRoute>
            <ReturnDetails email={email} onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/tickets" element={
          <ProtectedRoute>
            <Tickets email={email} onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/warehouses" element={
          <ProtectedRoute>
            <Warehouses email={email} onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/warehouses/add" element={
          <ProtectedRoute>
            <AddWarehouse email={email} onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/warehouses/:warehouseId" element={
          <ProtectedRoute>
            <EditWarehouse email={email} onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/integrations" element={
          <ProtectedRoute>
            <Integrations email={email} onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/onboarding" element={
          <ProtectedRoute>
            <Onboarding email={email} onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <AdminRoute>
            <Admin email={email} onLogout={handleLogout} />
          </AdminRoute>
        } />
        <Route path="/admin/payouts" element={
          <AdminRoute>
            <Payouts email={email} onLogout={handleLogout} />
          </AdminRoute>
        } />
        <Route path="/celebs" element={
          <AdminRoute>
            <CelebritiesManager email={email} onLogout={handleLogout} />
          </AdminRoute>
        } />
        <Route path="/admin/celebrities" element={<Navigate to="/celebs" replace />} />
        <Route path="/admin/celebs" element={<Navigate to="/celebs" replace />} />
        <Route path="/brands" element={
          <AdminRoute>
            <BrandsOnboarding email={email} onLogout={handleLogout} />
          </AdminRoute>
        } />
        <Route path="/brands/:brandId/gallery" element={
          <AdminRoute>
            <BrandGallery email={email} onLogout={handleLogout} />
          </AdminRoute>
        } />
        <Route path="/admin/brands" element={<Navigate to="/brands" replace />} />
        <Route path="/admin/brands/:brandId/gallery" element={
          <AdminRoute>
            <BrandGallery email={email} onLogout={handleLogout} />
          </AdminRoute>
        } />
        {/* Products & Category Taxonomy Routes */}
        <Route path="/products" element={
          <AdminRoute>
            <ProductsManager email={email} onLogout={handleLogout} />
          </AdminRoute>
        } />
        <Route path="/products/:superCategory" element={
          <AdminRoute>
            <ProductsManager email={email} onLogout={handleLogout} />
          </AdminRoute>
        } />
        <Route path="/products/:superCategory/:category" element={
          <AdminRoute>
            <ProductsManager email={email} onLogout={handleLogout} />
          </AdminRoute>
        } />
        <Route path="/admin/products" element={<Navigate to="/products" replace />} />
        {/* Outfit Studio Routes */}
        <Route path="/outfits" element={
          <AdminRoute>
            <OutfitAdminGetStarted email={email} onLogout={handleLogout} />
          </AdminRoute>
        } />
        <Route path="/outfits/get-started" element={
          <AdminRoute>
            <OutfitAdminGetStarted email={email} onLogout={handleLogout} />
          </AdminRoute>
        } />
        <Route path="/outfits/keywords-blacklist" element={
          <AdminRoute>
            <KeywordsBlacklist email={email} onLogout={handleLogout} />
          </AdminRoute>
        } />
        <Route path="/outfits/cci-blacklist" element={
          <AdminRoute>
            <CCIBlacklist email={email} onLogout={handleLogout} />
          </AdminRoute>
        } />
        <Route path="/outfits/trends" element={
          <AdminRoute>
            <TrendBuilder email={email} onLogout={handleLogout} />
          </AdminRoute>
        } />
        <Route path="/admin/outfits" element={<Navigate to="/outfits" replace />} />
        <Route path="/admin/outfits/*" element={<Navigate to="/outfits" replace />} />
        <Route path="/orders/:orderId/:itemHandle/:itemVariantId" element={
          <ProtectedRoute>
            <OrderItemDetails email={email} onLogout={handleLogout} />
          </ProtectedRoute>
        } />

        {/* Default Routes */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </GoogleMapsProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
